import type { AlertNoteVariant } from '@d-orca/shared/alerts';
import { authedFetch, UnauthorizedError } from '@apis/auth';
import type { IApiError } from '@interfaces/IApiError';

/** Every alert type, as a runtime array so the union derives from ONE source
 *  and a runtime guard (isActivityAlertType) can validate untrusted input. */
export const ALL_ALERT_TYPES = [
  'overdue', 'due-soon-3d', 'due-soon-1d', 'risk-up', 'delayed', 'stalled',
  'issue-overdue',
  'issue-opened', 'issue-due-soon', 'issue-rescheduled', 'issue-priority-raised',
  'issue-stalled', 'issue-resolved', 'issue-reopened',
  'completed', 'milestone-reached', 'milestone-missed', 'rescheduled', 'scope-added',
  // Deliveries & Stock: one group per red/amber note the inventory panel draws.
  'stock-gap', 'stock-ran-out', 'delivery-late', 'stock-untracked',
  'supplier-declined',
] as const;
export type ActivityAlertType = (typeof ALL_ALERT_TYPES)[number];
export type AlertSeverity = 'critical' | 'warning' | 'info';
export type { AlertNoteVariant } from '@d-orca/shared/alerts';


/** Runtime narrow for untrusted input (e.g. a URL param) to a valid alert type. */
export function isActivityAlertType(value: string | null | undefined): value is ActivityAlertType {
  return value != null && (ALL_ALERT_TYPES as readonly string[]).includes(value);
}

/** Alert types that read as good news — coloured green and sorted into the
 *  success band. Single source of truth shared by the grouping sort
 *  (alertGrouping.ts) and the card's severity classes, so the two can't drift. */
export const SUCCESS_ALERT_TYPES: ReadonlySet<ActivityAlertType> = new Set([
  'completed',
  'milestone-reached',
  'issue-resolved',
]);

export interface IActivityAlertItem {
  scheduleNumber: string;
  taskId: string;
  type: ActivityAlertType;
  severity: AlertSeverity;
  title: string;
  /** Days overdue / days left / % behind / days stalled — and for
   *  completed/milestone-reached, days early (+) or late (−). */
  detailValue: number;
  /* Optional per-type context ('YYYY-MM-DD') the detail line states. Absent
     on rows written before these fields existed — copy falls back undated. */
  /** scope-added: planned start. */
  startDate?: string;
  /** The planned end / due date the alert is about. */
  endDate?: string;
  /** rescheduled: the end it moved FROM. */
  previousEndDate?: string;
  /** stalled: progress frozen since this day. */
  sinceDate?: string;
  /** risk-up: previous level rank; delayed: plan-expected percent. */
  previousValue?: number;
  /** On the schedule's critical path (same CPM solve the pages run). */
  isCritical?: boolean;
  firedAt: string;
  /** Set only on issue alerts, which belong to a Trusstor issue rather than a schedule activity. */
  issueId?: string;
  /** Set only on issue alerts: whether the issue is flagged as a safety issue. */
  isSafety?: boolean;
  /** Set on logistics alerts keyed to a material (stock-gap) —
   *  the inventory row's name at the moment the alert fired. */
  materialName?: string;
  /** Set on stock-gap alerts — the level's daysLeft (days of
   *  stock remaining) at the moment the alert fired. detailValue is NOT the
   *  stock-days figure: detailValue carries the gap, while on
   *  stock-gap the gap (lead − daysLeft). */
  stockDaysLeft?: number;
  /** The order the note names — an unsent draft, a late delivery, or the order
   *  behind an untracked material. */
  deliveryRef?: string;
  /** WHICH of a Deliveries & Stock group's notes this alert repeats. The card
   *  renders the inventory row's own sentence from it, so the alert and the row
   *  say the same thing; the text itself is never stored (the sweep writes at
   *  night, and two readers may open it in different languages). */
  noteVariant?: AlertNoteVariant;
}

export interface IActivityAlertsFeed {
  alerts: IActivityAlertItem[];
  newToday: number;
  /** When the site's alerts were last produced; null before its first sweep. */
  sweptAt: string | null;
}

export async function fetchActivityAlerts(
  siteId: string,
  signal?: AbortSignal,
  limit = 100,
): Promise<IActivityAlertsFeed> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/activity-alerts?limit=${limit}`,
    { signal },
  );
  if (res.ok) return (await res.json()) as IActivityAlertsFeed;
  if (res.status === 401) throw new UnauthorizedError();
  const body = (await res.json().catch(() => null)) as IApiError | null;
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

export interface IAlertsFreshness {
  /** When the nightly sweep last ran for this site (diagnostic). */
  sweptAt: string | null;
  /** `firedAt` of the site's newest alert — moves only when a sweep actually
   *  produced something, which is what the "changes available" notice keys on. */
  latestFiredAt: string | null;
}

/** Staleness probe: two timestamps, no alert payload. Fetched only at
 *  reader-caused moments (page load, tab refocus) — never on a timer. */
export async function fetchAlertsFreshness(
  siteId: string,
  signal?: AbortSignal,
): Promise<IAlertsFreshness> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/activity-alerts/last-swept`,
    { signal },
  );
  if (res.ok) return (await res.json()) as IAlertsFreshness;
  if (res.status === 401) throw new UnauthorizedError();
  const body = (await res.json().catch(() => null)) as IApiError | null;
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}
